/**
 * Sandbox — the monitoring surface over the code-sandbox sidecar. Two tabs:
 *
 * - **Esecuzioni** — every Python run the sandbox executed (compute, custom tools,
 *   workflow `sandbox_code`), newest first, with full detail (code / stdout / stderr
 *   / result / output files).
 * - **Sessioni** — the persistent per-conversation WORKSPACES (size / file count /
 *   TTL) and their file listing.
 *
 * Deliberately DISTINCT from the LLM "Esecuzioni"/"Sessioni" pages (those are the
 * `llm_usage` ledger — model turns): THESE are sandbox CODE runs + workspace dirs.
 * The data comes from a THIN PROXY (`admin/routes/ai_sandbox.py`, gated `odino:use`)
 * over the sidecar's own `/v1/executions` + `/v1/sessions` read APIs; file downloads
 * stream THROUGH Tray so the sidecar token never reaches the browser.
 *
 * **Graceful-off:** when the sidecar is unconfigured (`HUB_SANDBOX_SERVICE_URL`
 * unset) the list responses carry `sandbox_configured: false` → a "non configurata"
 * empty state instead of an error. Read-only; under the `ai` plane. Native `<select>`
 * is intentional (operator console). Cursor pagination (the sidecar is cursor-based,
 * not page-numbered): a cursor stack drives Prec./Succ.
 */
import type { ReactNode } from "react";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Boxes,
  ChevronLeft,
  ChevronRight,
  Download,
  FileCode2,
  FolderOpen,
  TerminalSquare,
} from "lucide-react";

import { cn } from "@/lib/cn";
import { sandboxApi } from "@/lib/api-ai";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogBody } from "@/components/ui/dialog";
import { EmptyState, PageContainer, PageContent, PageHeader } from "@/components/ui/page";
import { Table, Tbody, Td, Th, Thead, Tr } from "@/components/ui/table";

const inputCls = "rounded-md border border-border bg-background px-2 py-1.5 text-sm";
const PAGE_SIZES = [25, 50, 100] as const;
const ORIGINS = ["api", "workflow", "user_tool", "agent"] as const;

type Tab = "executions" | "sessions";

// --- formatting helpers -----------------------------------------------------

function fmtWhen(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleString("it-IT");
}

function fmtDuration(ms: number | null): string {
  if (ms == null) return "—";
  if (ms < 1000) return `${ms} ms`;
  return `${(ms / 1000).toLocaleString("it-IT", { maximumFractionDigits: 1 })} s`;
}

function fmtBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

/** A run/session status → a tone tint for the badge. */
function statusTint(status: string): string {
  switch (status) {
    case "succeeded":
    case "success":
    case "active":
      return "bg-success/10 text-success";
    case "failed":
    case "error":
    case "timeout":
      return "bg-danger/10 text-danger";
    case "running":
      return "bg-info/10 text-info";
    default:
      return "bg-muted text-muted-foreground";
  }
}

/** The origin → a faint tint so the eye groups runs by what fired them. */
function originTint(origin: string | null): string {
  switch (origin) {
    case "agent":
      return "bg-accent/10 text-accent";
    case "workflow":
      return "bg-info/10 text-info";
    case "user_tool":
      return "bg-warning/10 text-warning";
    default:
      return "bg-muted text-muted-foreground";
  }
}

// --- shared "non configurata" empty state -----------------------------------

function NotConfigured() {
  return (
    <EmptyState
      icon={Boxes}
      title="Sandbox non configurata"
      description="Il servizio sandbox non è attivo (HUB_SANDBOX_SERVICE_URL non impostato). Esecuzioni e sessioni del codice compaiono qui quando la sandbox è collegata."
    />
  );
}

// --- page shell -------------------------------------------------------------

export function SandboxPage() {
  const [tab, setTab] = useState<Tab>("executions");

  return (
    <PageContainer>
      <PageHeader
        title="Sandbox"
        description="Le esecuzioni di codice della sandbox (compute, strumenti personalizzati, nodo sandbox_code) e gli spazi di lavoro persistenti. Distinte dalle Esecuzioni/Sessioni LLM (quelle sono i turni del modello)."
      />
      <PageContent>
      {/* Tab toggle ------------------------------------------------------- */}
      <div className="mb-4 inline-flex rounded-md border border-border p-0.5 text-sm">
        <button
          type="button"
          className={cn(
            "inline-flex items-center gap-1.5 rounded px-3 py-1.5",
            tab === "executions" ? "bg-muted font-medium" : "text-muted-foreground hover:text-foreground",
          )}
          onClick={() => setTab("executions")}
        >
          <TerminalSquare className="h-3.5 w-3.5" /> Esecuzioni
        </button>
        <button
          type="button"
          className={cn(
            "inline-flex items-center gap-1.5 rounded px-3 py-1.5",
            tab === "sessions" ? "bg-muted font-medium" : "text-muted-foreground hover:text-foreground",
          )}
          onClick={() => setTab("sessions")}
        >
          <FolderOpen className="h-3.5 w-3.5" /> Sessioni
        </button>
      </div>

      {tab === "executions" ? <ExecutionsTab /> : <SessionsTab />}
      </PageContent>
    </PageContainer>
  );
}

// --- shared cursor pager ----------------------------------------------------

/** A minimal cursor pager: a stack of cursors (first page = `[null]`). `current`
 *  is the top; `push(next)` appends; `pop()` goes back. Reset on filter change. */
function useCursorStack() {
  const [stack, setStack] = useState<(string | null)[]>([null]);
  return {
    current: stack[stack.length - 1] ?? null,
    canPrev: stack.length > 1,
    push: (next: string | null) => next != null && setStack((s) => [...s, next]),
    pop: () => setStack((s) => (s.length > 1 ? s.slice(0, -1) : s)),
    reset: () => setStack([null]),
  };
}

function Pager({
  count,
  hasNext,
  canPrev,
  onPrev,
  onNext,
  limit,
  onLimit,
  noun,
}: {
  count: number;
  hasNext: boolean;
  canPrev: boolean;
  onPrev: () => void;
  onNext: () => void;
  limit: number;
  onLimit: (n: number) => void;
  noun: string;
}) {
  return (
    <div className="mt-4 flex items-center justify-between gap-3 text-sm text-muted-foreground">
      <div className="flex items-center gap-2">
        <span>
          {count} {noun}
        </span>
        <select className={inputCls} value={limit} onChange={(e) => onLimit(Number(e.target.value))}>
          {PAGE_SIZES.map((s) => (
            <option key={s} value={s}>
              {s} per pagina
            </option>
          ))}
        </select>
      </div>
      <div className="flex items-center gap-2">
        <button
          type="button"
          className="inline-flex items-center gap-1 rounded-md border border-border px-2 py-1 hover:bg-muted disabled:opacity-40"
          disabled={!canPrev}
          onClick={onPrev}
        >
          <ChevronLeft className="h-3.5 w-3.5" /> Prec.
        </button>
        <button
          type="button"
          className="inline-flex items-center gap-1 rounded-md border border-border px-2 py-1 hover:bg-muted disabled:opacity-40"
          disabled={!hasNext}
          onClick={onNext}
        >
          Succ. <ChevronRight className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}

// --- Executions tab ---------------------------------------------------------

function ExecutionsTab() {
  const [origin, setOrigin] = useState("");
  const [status, setStatus] = useState("");
  const [language, setLanguage] = useState("");
  const [q, setQ] = useState("");
  const [limit, setLimit] = useState<(typeof PAGE_SIZES)[number]>(50);
  const [selected, setSelected] = useState<string | null>(null);
  const pager = useCursorStack();

  const filters = {
    origin: origin || null,
    status: status || null,
    language: language || null,
    q: q || null,
    limit,
  };
  const q1 = useQuery({
    queryKey: ["ai", "sandbox", "executions", filters, pager.current],
    queryFn: () => sandboxApi.listExecutions({ ...filters, cursor: pager.current }),
  });

  const data = q1.data;
  const rows = data?.executions ?? [];
  const configured = data?.sandbox_configured ?? true;

  /** Any filter change rewinds to the first page (cursor stack invalidated). */
  function onFilter<T>(set: (v: T) => void) {
    return (v: T) => {
      set(v);
      pager.reset();
    };
  }

  return (
    <>
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <select className={inputCls} value={origin} onChange={(e) => onFilter(setOrigin)(e.target.value)}>
          <option value="">Tutte le origini</option>
          {ORIGINS.map((o) => (
            <option key={o} value={o}>
              {o}
            </option>
          ))}
        </select>
        <select className={inputCls} value={status} onChange={(e) => onFilter(setStatus)(e.target.value)}>
          <option value="">Tutti gli stati</option>
          <option value="succeeded">succeeded</option>
          <option value="failed">failed</option>
          <option value="timeout">timeout</option>
        </select>
        <input
          className={inputCls}
          value={language}
          onChange={(e) => onFilter(setLanguage)(e.target.value)}
          placeholder="Linguaggio…"
        />
        <input
          className={inputCls}
          value={q}
          onChange={(e) => onFilter(setQ)(e.target.value)}
          placeholder="Cerca nel codice…"
        />
      </div>

      {q1.isLoading && <p className="text-sm text-muted-foreground">Caricamento…</p>}
      {q1.isError && !q1.isLoading && (
        <p className="text-sm text-danger">Impossibile caricare le esecuzioni della sandbox.</p>
      )}

      {!q1.isLoading && !q1.isError && !configured ? (
        <NotConfigured />
      ) : !q1.isLoading && !q1.isError && rows.length === 0 ? (
        <EmptyState
          icon={TerminalSquare}
          title="Nessuna esecuzione"
          description="Nessuna esecuzione di codice della sandbox per i filtri selezionati."
        />
      ) : (
        rows.length > 0 && (
          <div className="overflow-hidden rounded-lg border border-border/60">
            <Table>
              <Thead>
                <Tr>
                  <Th>Avvio</Th>
                  <Th>Linguaggio</Th>
                  <Th>Origine</Th>
                  <Th>Stato</Th>
                  <Th className="text-right">Durata</Th>
                  <Th className="text-right">File</Th>
                  <Th>Codice</Th>
                </Tr>
              </Thead>
              <Tbody>
                {rows.map((r) => (
                  <Tr
                    key={r.execution_id}
                    className="cursor-pointer"
                    onClick={() => setSelected(r.execution_id)}
                  >
                    <Td className="whitespace-nowrap text-muted-foreground">{fmtWhen(r.started_at)}</Td>
                    <Td className="font-mono text-xs">{r.language}</Td>
                    <Td>
                      {r.origin && (
                        <span className={cn("inline-block rounded px-1.5 py-0.5 text-[10px] font-medium", originTint(r.origin))}>
                          {r.origin}
                        </span>
                      )}
                    </Td>
                    <Td>
                      <span className={cn("inline-block rounded px-1.5 py-0.5 text-[10px] font-medium", statusTint(r.status))}>
                        {r.status}
                      </span>
                    </Td>
                    <Td className="text-right tabular-nums text-muted-foreground">{fmtDuration(r.duration_ms)}</Td>
                    <Td className="text-right tabular-nums text-muted-foreground">{r.output_files.length || "—"}</Td>
                    <Td className="max-w-xs truncate font-mono text-[11px] text-muted-foreground">
                      {r.code_preview}
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </div>
        )
      )}

      {configured && rows.length > 0 && (
        <Pager
          count={rows.length}
          hasNext={!!data?.next_cursor}
          canPrev={pager.canPrev}
          onPrev={pager.pop}
          onNext={() => pager.push(data?.next_cursor ?? null)}
          limit={limit}
          onLimit={(n) => onFilter(setLimit)(n as (typeof PAGE_SIZES)[number])}
          noun="in pagina"
        />
      )}

      {selected && <ExecutionDialog id={selected} onClose={() => setSelected(null)} />}
    </>
  );
}

function ExecutionDialog({ id, onClose }: { id: string; onClose: () => void }) {
  const q = useQuery({
    queryKey: ["ai", "sandbox", "execution", id],
    queryFn: () => sandboxApi.getExecution(id),
  });
  const e = q.data;

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileCode2 className="h-4 w-4" /> Esecuzione sandbox
          </DialogTitle>
        </DialogHeader>
        <DialogBody className="max-h-[70vh] space-y-4 overflow-y-auto">
          {q.isLoading && <p className="text-sm text-muted-foreground">Caricamento…</p>}
          {q.isError && <p className="text-sm text-danger">Impossibile caricare il dettaglio.</p>}
          {e && (
            <>
              <dl className="grid grid-cols-2 gap-x-6 gap-y-1 text-xs sm:grid-cols-3">
                <Detail label="ID" value={e.execution_id} mono />
                <Detail label="Stato" value={e.status} />
                <Detail label="Linguaggio" value={e.language} />
                <Detail label="Durata" value={fmtDuration(e.duration_ms)} />
                <Detail label="Exit code" value={e.exit_code == null ? "—" : String(e.exit_code)} />
                <Detail label="Origine" value={e.origin ?? "—"} />
                <Detail label="Avvio" value={fmtWhen(e.started_at)} />
                <Detail label="Fine" value={fmtWhen(e.finished_at)} />
                <Detail label="Sessione" value={e.session_id ?? "—"} mono />
              </dl>

              <Section title="Codice">
                <Code text={e.code} />
              </Section>
              {e.stdout && (
                <Section title={`stdout${e.stdout_truncated ? " (troncato)" : ""}`}>
                  <Code text={e.stdout} />
                </Section>
              )}
              {e.stderr && (
                <Section title={`stderr${e.stderr_truncated ? " (troncato)" : ""}`}>
                  <Code text={e.stderr} danger />
                </Section>
              )}
              {e.result != null && (
                <Section title="Risultato">
                  <Code text={typeof e.result === "string" ? e.result : JSON.stringify(e.result, null, 2)} />
                </Section>
              )}
              {e.output_files.length > 0 && (
                <Section title="File di output">
                  <ul className="space-y-1">
                    {e.output_files.map((f) => (
                      <li key={f.name} className="flex items-center justify-between gap-2 text-xs">
                        <span className="font-mono">{f.name}</span>
                        <button
                          type="button"
                          className="inline-flex items-center gap-1 rounded-md border border-border px-2 py-0.5 hover:bg-muted"
                          onClick={() => void sandboxApi.downloadExecutionFile(e.execution_id, f.name)}
                        >
                          <Download className="h-3 w-3" /> {fmtBytes(f.size_bytes)}
                        </button>
                      </li>
                    ))}
                  </ul>
                </Section>
              )}
            </>
          )}
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
}

// --- Sessions tab -----------------------------------------------------------

function SessionsTab() {
  const [status, setStatus] = useState("");
  const [limit, setLimit] = useState<(typeof PAGE_SIZES)[number]>(50);
  const [selected, setSelected] = useState<string | null>(null);
  const pager = useCursorStack();

  const filters = { status: status || null, limit };
  const q1 = useQuery({
    queryKey: ["ai", "sandbox", "sessions", filters, pager.current],
    queryFn: () => sandboxApi.listSessions({ ...filters, cursor: pager.current }),
  });

  const data = q1.data;
  const rows = data?.sessions ?? [];
  const configured = data?.sandbox_configured ?? true;

  function onFilter<T>(set: (v: T) => void) {
    return (v: T) => {
      set(v);
      pager.reset();
    };
  }

  return (
    <>
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <select className={inputCls} value={status} onChange={(e) => onFilter(setStatus)(e.target.value)}>
          <option value="">Tutti gli stati</option>
          <option value="active">active</option>
          <option value="deleted">deleted</option>
        </select>
      </div>

      {q1.isLoading && <p className="text-sm text-muted-foreground">Caricamento…</p>}
      {q1.isError && !q1.isLoading && (
        <p className="text-sm text-danger">Impossibile caricare le sessioni della sandbox.</p>
      )}

      {!q1.isLoading && !q1.isError && !configured ? (
        <NotConfigured />
      ) : !q1.isLoading && !q1.isError && rows.length === 0 ? (
        <EmptyState
          icon={FolderOpen}
          title="Nessuna sessione"
          description="Nessuno spazio di lavoro persistente della sandbox per i filtri selezionati."
        />
      ) : (
        rows.length > 0 && (
          <div className="overflow-hidden rounded-lg border border-border/60">
            <Table>
              <Thead>
                <Tr>
                  <Th>Sessione</Th>
                  <Th>Stato</Th>
                  <Th>Creata</Th>
                  <Th>Scadenza</Th>
                  <Th className="text-right">File</Th>
                  <Th className="text-right">Esecuzioni</Th>
                  <Th className="text-right">Dimensione</Th>
                </Tr>
              </Thead>
              <Tbody>
                {rows.map((r) => (
                  <Tr
                    key={r.session_id}
                    className="cursor-pointer"
                    onClick={() => setSelected(r.session_id)}
                  >
                    <Td className="max-w-[14rem] truncate font-mono text-xs">{r.session_id}</Td>
                    <Td>
                      <span className={cn("inline-block rounded px-1.5 py-0.5 text-[10px] font-medium", statusTint(r.status))}>
                        {r.status}
                      </span>
                    </Td>
                    <Td className="whitespace-nowrap text-muted-foreground">{fmtWhen(r.created_at)}</Td>
                    <Td className="whitespace-nowrap text-muted-foreground">{fmtWhen(r.expires_at)}</Td>
                    <Td className="text-right tabular-nums text-muted-foreground">{r.file_count}</Td>
                    <Td className="text-right tabular-nums text-muted-foreground">{r.execution_count}</Td>
                    <Td className="text-right tabular-nums text-muted-foreground">{fmtBytes(r.workspace_size_bytes)}</Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </div>
        )
      )}

      {configured && rows.length > 0 && (
        <Pager
          count={rows.length}
          hasNext={!!data?.next_cursor}
          canPrev={pager.canPrev}
          onPrev={pager.pop}
          onNext={() => pager.push(data?.next_cursor ?? null)}
          limit={limit}
          onLimit={(n) => onFilter(setLimit)(n as (typeof PAGE_SIZES)[number])}
          noun="in pagina"
        />
      )}

      {selected && <SessionDialog id={selected} onClose={() => setSelected(null)} />}
    </>
  );
}

function SessionDialog({ id, onClose }: { id: string; onClose: () => void }) {
  const q = useQuery({
    queryKey: ["ai", "sandbox", "session", id],
    queryFn: () => sandboxApi.getSession(id),
  });
  const s = q.data;

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FolderOpen className="h-4 w-4" /> Sessione sandbox
          </DialogTitle>
        </DialogHeader>
        <DialogBody className="max-h-[70vh] space-y-4 overflow-y-auto">
          {q.isLoading && <p className="text-sm text-muted-foreground">Caricamento…</p>}
          {q.isError && <p className="text-sm text-danger">Impossibile caricare il dettaglio.</p>}
          {s && (
            <>
              <dl className="grid grid-cols-2 gap-x-6 gap-y-1 text-xs sm:grid-cols-3">
                <Detail label="ID" value={s.session_id} mono />
                <Detail label="Stato" value={s.status} />
                <Detail label="TTL" value={`${s.ttl_seconds} s`} />
                <Detail label="Creata" value={fmtWhen(s.created_at)} />
                <Detail label="Ultimo accesso" value={fmtWhen(s.last_accessed_at)} />
                <Detail label="Scadenza" value={fmtWhen(s.expires_at)} />
                <Detail label="File" value={String(s.file_count)} />
                <Detail label="Esecuzioni" value={String(s.execution_count)} />
                <Detail label="Dimensione" value={fmtBytes(s.workspace_size_bytes)} />
              </dl>

              {Object.keys(s.metadata ?? {}).length > 0 && (
                <Section title="Metadati">
                  <Code text={JSON.stringify(s.metadata, null, 2)} />
                </Section>
              )}

              <Section title={`File dello spazio di lavoro${s.workspace_files_truncated ? " (elenco troncato)" : ""}`}>
                {s.workspace_files.length === 0 ? (
                  <p className="text-xs text-muted-foreground">Nessun file.</p>
                ) : (
                  <ul className="space-y-1">
                    {s.workspace_files.map((f) => (
                      <li key={f.name} className="flex items-center justify-between gap-2 text-xs">
                        <span className="font-mono">{f.name}</span>
                        <button
                          type="button"
                          className="inline-flex items-center gap-1 rounded-md border border-border px-2 py-0.5 hover:bg-muted"
                          onClick={() => void sandboxApi.downloadSessionFile(s.session_id, f.name)}
                        >
                          <Download className="h-3 w-3" /> {fmtBytes(f.size_bytes)}
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </Section>
            </>
          )}
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
}

// --- small presentational bits ----------------------------------------------

function Detail({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div>
      <dt className="text-[10px] uppercase tracking-wide text-muted-foreground/70">{label}</dt>
      <dd className={cn("break-all text-foreground", mono && "font-mono")}>{value}</dd>
    </div>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div>
      <h3 className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">{title}</h3>
      {children}
    </div>
  );
}

function Code({ text, danger }: { text: string; danger?: boolean }) {
  return (
    <pre
      className={cn(
        "max-h-64 overflow-auto rounded-md border border-border bg-surface/40 p-2 text-[11px] leading-relaxed",
        danger ? "text-danger" : "text-foreground",
      )}
    >
      {text}
    </pre>
  );
}
